import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { addAuthHeaders, apiPath } from '../../shared/api';
import { config } from '../../shared/config/transport';
import { InvoiceBasis } from '../types/invoiceBasis';
import { Company } from '../../shared/types/company';
import { CompanyAccessFallback, useCompanyAccessWithQuery } from '../../shared/hooks/companyAccess';

// Define a service using a base URL and expected endpoints
export const invoicingApi = createApi({
  reducerPath: 'invoicing',
  baseQuery: fetchBaseQuery({
    baseUrl: apiPath(config, '/'),
    prepareHeaders: (h) => addAuthHeaders(config, h), // Adds msal auth headers
  }),
  tagTypes: [
    'invoicing',
    'company',
  ],
  endpoints: (builder) => ({
    getAll: builder.query<InvoiceBasis[], void>({
      query: () => 'invoiceBasis',
      providesTags: ['invoicing'],
    }),
    getDetails: builder.query<InvoiceBasis, { uid: string }>({
      query: ({ uid }) => `invoiceBasis/${uid}`,
      providesTags: (result) => ['invoicing', { type: 'invoicing', id: result?.uid }],
    }),
    create: builder.mutation<void, void>({
      query: () => ({
        url: 'invoiceBasis/create',
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['invoicing'],
    }),
    lock: builder.mutation<InvoiceBasis, { uid: string }>({
      query: ({ uid }) => ({
        url: `invoiceBasis/${uid}/lock`,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['invoicing'],
    }),
    getMyCompany: builder.query<Company | undefined, void>({
      async queryFn(_arg, _queryApi, _extraOptions, baseQueryFetch) {
        const result = await baseQueryFetch({
          url: 'me/company',
        });

        const error = result.error as any;
        if (error?.status === 404 || error?.originalStatus === 404) {
          return { data: undefined };
        }

        return result.data
          ? { data: result.data as Company }
          : { error: result.error };
      },
      providesTags: ['company'],
    }),
    setMyCompany: builder.mutation<void, string>({
      query: (companyName) => ({
        url: 'me/company',
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: { companyName },
        responseHandler: 'text',
      }),
      transformResponse: () => undefined,
      invalidatesTags: ['company', 'invoicing'],
    }),
    getCompanyList: builder.query<string[], void>({
      query: () => 'companies',
      providesTags: ['company'],
    }),
  }),
});

export default invoicingApi.reducer;

// Transportation-specific hook using transportation API
export const useCompanyAccessTransport = (fallback: CompanyAccessFallback = {}) => useCompanyAccessWithQuery(() => invoicingApi.useGetMyCompanyQuery(), fallback);

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetAllQuery,
  useGetDetailsQuery,
  useCreateMutation,
  useLockMutation,
  useGetMyCompanyQuery,
  useSetMyCompanyMutation,
  useGetCompanyListQuery,
} = invoicingApi;
